import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Upload, Receipt, Check, X, Fuel, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const Receipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [vehicleId, setVehicleId] = useState('');
  const [type, setType] = useState('fuel');
  const [amount, setAmount] = useState('');
  const [kmReading, setKmReading] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [receiptsRes, vehiclesRes] = await Promise.all([
        axios.get('/receipts'),
        axios.get('/vehicles')
      ]);
      // Handle both old and new API format
      setReceipts(receiptsRes.data.receipts || receiptsRes.data);
      setVehicles(vehiclesRes.data.vehicles || vehiclesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !vehicleId || !amount) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    if (type === 'fuel' && !kmReading) {
      toast.error('Kilometerstand ist für Tankbelege erforderlich');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('vehicle_id', vehicleId);
    formData.append('type', type);
    formData.append('amount', amount);
    if (kmReading) {
      formData.append('km_reading', kmReading);
    }

    try {
      await axios.post('/receipts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Beleg erfolgreich hochgeladen');
      setUploadOpen(false);
      setFile(null);
      setVehicleId('');
      setAmount('');
      setKmReading('');
      loadData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="receipts-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Belege
          </h1>
          <p className="text-zinc-400">Tank- und Einkaufsbelege verwalten</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="upload-receipt-btn">
              <Upload className="mr-2 h-4 w-4" />
              Beleg hochladen
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Beleg hochladen</DialogTitle>
              <DialogDescription>
                Foto von Tank- oder Einkaufsbeleg hochladen
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="file">Beleg-Foto *</Label>
                <Input 
                  id="file"
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="file-input"
                />
              </div>
              <div>
                <Label htmlFor="vehicle">Fahrzeug *</Label>
                <Select value={vehicleId} onValueChange={setVehicleId}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="vehicle-select">
                    <SelectValue placeholder="Fahrzeug auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {vehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.license_plate} - {v.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="type">Typ *</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="fuel">Tanken</SelectItem>
                    <SelectItem value="purchase">Einkauf</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Betrag (€) *</Label>
                <Input 
                  id="amount"
                  type="number" 
                  step="0.01"
                  value={amount} 
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="amount-input"
                />
              </div>
              {type === 'fuel' && (
                <div>
                  <Label htmlFor="km">Kilometerstand *</Label>
                  <Input 
                    id="km"
                    type="number" 
                    value={kmReading} 
                    onChange={(e) => setKmReading(e.target.value)}
                    placeholder="Aktueller Kilometerstand"
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="km-input"
                  />
                </div>
              )}
              <Button onClick={handleUpload} className="w-full" data-testid="confirm-upload-btn">
                Hochladen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {receipts.map(receipt => {
          const vehicle = vehicles.find(v => v.vehicle_id === receipt.vehicle_id);
          
          return (
            <Card 
              key={receipt.receipt_id} 
              className="bg-zinc-900 border-zinc-800"
              data-testid={`receipt-card-${receipt.receipt_id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-zinc-800 flex items-center justify-center">
                      {receipt.type === 'fuel' ? (
                        <Fuel className="h-6 w-6 text-zinc-400" />
                      ) : (
                        <ShoppingCart className="h-6 w-6 text-zinc-400" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold text-white">
                        {receipt.type === 'fuel' ? 'Tanken' : 'Einkauf'}
                      </CardTitle>
                      <p className="text-sm text-zinc-400">
                        {vehicle?.license_plate || 'Unbekannt'}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant={receipt.lexware_synced ? 'default' : 'secondary'}
                    className="flex items-center gap-1"
                  >
                    {receipt.lexware_synced ? (
                      <><Check className="h-3 w-3" /> Synced</>
                    ) : (
                      <><X className="h-3 w-3" /> Pending</>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Betrag:</span>
                  <span className="font-mono text-white font-medium">€ {receipt.amount.toFixed(2)}</span>
                </div>
                {receipt.km_reading && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Kilometerstand:</span>
                    <span className="font-mono text-white">{receipt.km_reading.toLocaleString()} km</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Datum:</span>
                  <span className="text-zinc-300">
                    {new Date(receipt.created_at).toLocaleDateString('de-DE')}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {receipts.length === 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="h-16 w-16 text-zinc-600 mb-4" />
            <p className="text-zinc-400 text-center">Noch keine Belege hochgeladen</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Receipts;
